import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { MessageCircle, X, Send, ArrowRight } from "lucide-react";

let _msgSeq = 0;
const nextMsgId = () => `m${Date.now()}-${_msgSeq++}`;

export default function SupportChat() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([{ id: nextMsgId(), role: "assistant", content: "Hi! I'm the Umbrella Assistant. Ask me about escrow, fees, vetting, or getting insured — or tell me what you need done and I'll help you post a job." }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [models, setModels] = useState([]);
  const [model, setModel] = useState("");
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open]);
  useEffect(() => {
    api.get("/ai/models").then((r) => { setModels(r.data.models || []); setModel(r.data.default || ""); }).catch(() => {});
  }, []);

  const goPostJob = (h) => {
    const p = new URLSearchParams();
    if (h.trade) p.set("trade", h.trade);
    if (h.description) p.set("desc", h.description);
    if (h.city) p.set("city", h.city);
    setOpen(false);
    navigate(`/post-job?${p.toString()}`);
  };

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    const next = [...msgs, { id: nextMsgId(), role: "user", content: text }];
    setMsgs(next); setInput(""); setBusy(true);
    try {
      const r = await api.post("/ai/chat", { message: text, history: next.slice(-7), model });
      setMsgs((m) => [...m, { id: nextMsgId(), role: "assistant", content: r.data.reply, handoff: r.data.handoff }]);
    } catch {
      setMsgs((m) => [...m, { id: nextMsgId(), role: "assistant", content: "Sorry, I'm having trouble right now. Please try again." }]);
    } finally { setBusy(false); }
  };

  return (
    <>
      <button data-testid="support-chat-toggle" onClick={() => setOpen((o) => !o)}
        className="fixed bottom-5 right-5 z-50 h-14 w-14 rounded-full bg-slate-950 hover:bg-amber-600 text-white shadow-xl flex items-center justify-center transition-colors">
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>
      {open && (
        <div data-testid="support-chat-panel" className="fixed bottom-24 right-5 z-50 w-[92vw] max-w-sm h-[28rem] rounded-2xl border border-slate-200 bg-white shadow-2xl flex flex-col overflow-hidden">
          <div className="bg-slate-950 text-white px-4 py-3 flex items-center justify-between gap-2">
            <span className="font-heading font-bold">Umbrella Assistant</span>
            {models.length > 1 && (
              <select data-testid="support-chat-model" value={model} onChange={(e) => setModel(e.target.value)}
                className="text-[11px] rounded-full bg-white/10 border border-white/20 text-white px-2 py-1 outline-none cursor-pointer">
                {models.map((m) => <option key={m.id} value={m.id} className="text-slate-900">{m.label}</option>)}
              </select>
            )}
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {msgs.map((m, i) => (
              <div key={m.id} data-testid={`support-msg-${i}`} className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.role === "user" ? "bg-slate-950 text-white" : "bg-slate-100 text-slate-800"}`}>{m.content}</div>
                {m.handoff && (
                  <button data-testid="chat-handoff-post-job" onClick={() => goPostJob(m.handoff)}
                    className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold px-3 py-1.5 transition-colors">
                    Post this job{m.handoff.trade ? ` (${m.handoff.trade})` : ""} <ArrowRight size={13} />
                  </button>
                )}
              </div>
            ))}
            {busy && <div className="text-xs text-slate-400 px-1">Assistant is typing…</div>}
            <div ref={endRef} />
          </div>
          <div className="p-2 border-t border-slate-100 flex gap-2">
            <input data-testid="support-chat-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask a question…" className="flex-1 rounded-full border border-slate-200 px-3 text-sm outline-none focus:border-amber-400" />
            <button data-testid="support-chat-send" onClick={send} disabled={busy} className="h-9 w-9 rounded-full bg-slate-950 hover:bg-amber-600 text-white flex items-center justify-center"><Send size={15} /></button>
          </div>
        </div>
      )}
    </>
  );
}
